/**
 * Copyright (c) 2023 Gitpod GmbH. All rights reserved.
 * Licensed under the GNU Affero General Public License (AGPL).
 * See License.AGPL.txt in the project root for license information.
 */

import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { type PropsWithClassName, cn } from "@/components/podkit/lib/cn";
import { ComboboxItem, ComboboxList } from "@ariakit/react";
import { ComboboxInput } from "@/components/podkit/forms/Input";

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & PropsWithClassName & { loading: boolean }
>(({ className, children, loading, ...props }, ref) => (
    <SelectPrimitive.Trigger
        ref={ref}
        className={cn(
            "flex w-full flex-col items-center",
            "rounded-lg px-4 py-[7px]",
            "text-sm text-content-primary placeholder:text-content-tertiary",
            "bg-surface-secondary",
            "border border-border-light",
            "text-base",
            "hover:[&_svg]:text-content-primary",
            "focus-visible:ring-ring focus:outline-none focus-visible:ring-1",
            "disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
            className,
        )}
        {...props}
    >
        <div className="flex w-full items-center justify-between gap-2">
            {children}
            <div className="flex-none">
                <SelectPrimitive.Icon asChild>
                    <ChevronDown size={16} className="text-content-tertiary" />
                </SelectPrimitive.Icon>
            </div>
        </div>
        {loading && (
            <span className="absolute animate-spin">
                <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle
                        cx="17"
                        cy="16.9999"
                        r="10.5"
                        transform="rotate(-45.6079 17 16.9999)"
                        stroke="#FF8A00"
                        strokeWidth="3"
                    />
                    <path
                        d="M10.0194 25.5598C5.28366 20.9235 5.20305 13.326 9.83936 8.59021"
                        stroke="white"
                        strokeWidth="4"
                    />
                </svg>
            </span>
        )}
    </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton> & PropsWithClassName
>(({ className, ...props }, ref) => (
    <SelectPrimitive.ScrollUpButton
        ref={ref}
        className={cn("flex cursor-default items-center justify-center py-1", className)}
        {...props}
    >
        <ChevronUp className="h-4 w-4" />
    </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton> & PropsWithClassName
>(({ className, ...props }, ref) => (
    <SelectPrimitive.ScrollDownButton
        ref={ref}
        className={cn("flex cursor-default items-center justify-center py-1", className)}
        {...props}
    >
        <ChevronDown className="h-4 w-4" />
    </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

function selectContentClasses(position: "popper" | "item-aligned") {
    return cn(
        "relative z-50 max-h-96 min-w-[8rem] overflow-hidden",
        "rounded-lg border border-border-base",
        "bg-surface-secondary text-content-primary shadow-md",
        "focus:ring-0",
        position === "popper" &&
            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
    );
}

const SelectContent = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & PropsWithClassName
>(({ className, children, position = "popper", ...props }, ref) => (
    <SelectPrimitive.Portal>
        <SelectPrimitive.Content
            ref={ref}
            className={cn(selectContentClasses(position), className)}
            position={position}
            {...props}
        >
            <SelectScrollUpButton />
            <SelectPrimitive.Viewport
                className={cn(
                    position === "popper" &&
                        "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
                )}
            >
                {children}
            </SelectPrimitive.Viewport>
            <SelectScrollDownButton />
        </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectContentCombobox = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & PropsWithClassName & { inputPlaceholder?: string }
>(({ className, children, inputPlaceholder, position = "popper", ...props }, ref) => (
    <SelectPrimitive.Portal>
        <SelectPrimitive.Content
            ref={ref}
            className={cn(
                selectContentClasses(position),
                // Ensure the content has the same width as the trigger
                "w-[var(--radix-select-trigger-width)]",
                // Ensures the hover-background doesn't break the rounded corners
                "overflow-x-hidden",
                className,
            )}
            position={position}
            {...props}
        >
            <div className="border-b border-border-light px-3 py-3">
                <ComboboxInput autoSelect placeholder={inputPlaceholder} />
            </div>

            <ScrollAreaPrimitive.Root className="h-full" type="auto">
                <SelectPrimitive.Viewport
                    asChild
                    className={cn("max-h-[min(320px,calc(var(--radix-popper-available-height)-61px))]")}
                >
                    {/* We're setting overflowY to undefined here to resolve a conflict where both SelectPrimitive.Viewport and ScrollAreaPrimitive.Viewport are
                        trying to set the overflow property, but one is using the short-hand and the other the non-shorthand (`overflow-y` vs `overflow`).
                        This disables the short-hand and rely on the non-shorthand instead.

                        This gets rid of the following warning message from React:
                        > Updating a style property during rerender (overflowY) when a conflicting property is set (overflow) can lead to styling bugs.

                        See https://github.com/radix-ui/primitives/issues/2059
                    */}
                    <ScrollAreaPrimitive.Viewport className="h-full w-full" style={{ overflowY: undefined }}>
                        <ComboboxList className="w-[var(--radix-select-trigger-width)]">{children}</ComboboxList>
                    </ScrollAreaPrimitive.Viewport>
                </SelectPrimitive.Viewport>
                <ScrollAreaPrimitive.Scrollbar className="px-[2px] py-[2px]" orientation="vertical">
                    <ScrollAreaPrimitive.Thumb
                        className="h-2 w-2 rounded-lg bg-surface-tertiary"
                        style={{ "--radix-scroll-area-thumb-width": "8px" } as React.CSSProperties}
                    />
                </ScrollAreaPrimitive.Scrollbar>
            </ScrollAreaPrimitive.Root>
        </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
));
SelectContentCombobox.displayName = SelectPrimitive.Content.displayName + " Combobox";

const SelectLabel = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Label>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label> & PropsWithClassName
>(({ className, ...props }, ref) => (
    <SelectPrimitive.Label ref={ref} className={cn("py-1.5 pl-8 pr-2 text-sm font-bold", className)} {...props} />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const selectItemClasses = cn(
    "relative flex w-full cursor-default select-none items-center",
    "rounded-sm py-1.5 pl-2 pr-2 text-sm outline-none",
    "hover:cursor-pointer",
    "focus:text-accent-foreground focus:bg-surface-secondary focus-visible:ring-0",
    "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
    "hover:text-accent-foreground hover:bg-surface-secondary",
    "data-[active-item]:bg-surface-tertiary",
);

const SelectItem = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & PropsWithClassName
>(({ className, children, ...props }, ref) => (
    <SelectPrimitive.Item ref={ref} className={cn(selectItemClasses, className)} {...props}>
        <SelectPrimitive.ItemText asChild={true}>{children}</SelectPrimitive.ItemText>
        <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
            <SelectPrimitive.ItemIndicator>
                <Check className="h-4 w-4" />
            </SelectPrimitive.ItemIndicator>
        </span>
    </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectItemCombobox = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & PropsWithClassName
>(({ className, children, ...props }, ref) => (
    <SelectPrimitive.Item ref={ref} className={cn(selectItemClasses, className)} {...props}>
        <ComboboxItem>
            <SelectPrimitive.ItemText asChild>{children}</SelectPrimitive.ItemText>
            <span className="absolute right-4 flex h-3.5 w-3.5 items-center justify-center">
                <SelectPrimitive.ItemIndicator>
                    <Check className="h-4 w-4" />
                </SelectPrimitive.ItemIndicator>
            </span>
        </ComboboxItem>
    </SelectPrimitive.Item>
));
SelectItemCombobox.displayName = SelectPrimitive.Item.displayName + " Combobox";

const SelectSeparator = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Separator>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator> & PropsWithClassName
>(({ className, ...props }, ref) => (
    <SelectPrimitive.Separator ref={ref} className={cn("-mx-1 my-1 h-px bg-border-base", className)} {...props} />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
    Select,
    SelectGroup,
    SelectValue,
    SelectTrigger,
    SelectContent,
    SelectContentCombobox,
    SelectLabel,
    SelectItem,
    SelectItemCombobox,
    SelectSeparator,
    SelectScrollUpButton,
    SelectScrollDownButton,
};
